/* =====================================================================
   tower/driver/src/scanner.js — the ONE live-camera scanner (scan-to-load S2; also the service stop and the office).

   window.raScan.open({ title, hint, onCode(code) }) shows a sheet with the camera running and a box to type the code.
   The camera reads QR, Code 128, Code 39 and EAN-13 with the browser's own BarcodeDetector; where there is none (iPhone
   Safari) ZXing for the browser (MIT, vendor/zxing-browser.min.js, pinned 0.1.5 in vendor/versions.lock.json) is loaded
   the first time it is needed — never before. No camera, a refused permission or no reader at all: the sheet says so in
   words and typing the code by hand always works. A code is handed over once, the camera is stopped, the sheet closes.
   Nothing is ever filled in that was not read or typed.
   ===================================================================== */
import { t } from './i18n.js';
import { esc } from './ui.js';

const FORMATS = ['qr_code', 'code_128', 'code_39', 'ean_13'];
const ZX_SRC = 'vendor/zxing-browser.min.js?v=0.1.5';
let _zx = null;
function loadZxing() {
  if (window.ZXingBrowser) return Promise.resolve(window.ZXingBrowser);
  if (_zx) return _zx;
  _zx = new Promise((res, rej) => {
    const s = document.createElement('script'); s.src = ZX_SRC; s.async = true;
    s.onload = () => (window.ZXingBrowser ? res(window.ZXingBrowser) : rej(new Error('no reader')));
    s.onerror = () => { _zx = null; rej(new Error('no reader')); };
    document.head.appendChild(s);
  });
  return _zx;
}

const S = { el: null, stream: null, stop: null, opts: null, done: false };

function close() {
  try { if (S.stop) S.stop(); } catch (e) {}
  try { if (S.stream) S.stream.getTracks().forEach((x) => x.stop()); } catch (e) {}
  S.stream = null; S.stop = null;
  if (S.el) S.el.remove();
  S.el = null;
}

function hand(code, via) {
  const c = String(code || '').trim();
  if (!c || S.done) return;
  S.done = true;
  const cb = S.opts && S.opts.onCode;
  close();
  try { if (navigator.vibrate) navigator.vibrate(40); } catch (e) {}
  if (typeof cb === 'function') cb(c, via || 'camera');   // 'typed' when it was keyed in by hand
}

function scanStatus(key) { const h = S.el && S.el.querySelector('[data-scan-status]'); if (h) h.textContent = t(key); }

async function camera() {
  const v = S.el && S.el.querySelector('video');
  if (!v) return;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { scanStatus('scan.noCamera'); return; }
  try {
    S.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
  } catch (e) { scanStatus(e && e.name === 'NotAllowedError' ? 'scan.cameraRefused' : 'scan.noCamera'); return; }
  if (!S.el) { S.stream.getTracks().forEach((x) => x.stop()); S.stream = null; return; }
  v.srcObject = S.stream;
  try { await v.play(); } catch (e) {}
  scanStatus('scan.aim');
  if ('BarcodeDetector' in window) {
    let det; try { det = new window.BarcodeDetector({ formats: FORMATS }); } catch (e) { try { det = new window.BarcodeDetector(); } catch (x) { det = null; } }
    if (det) {
      let live = true; S.stop = () => { live = false; };
      const tick = async () => {
        if (!live || !S.el) return;
        try { const got = await det.detect(v); if (got && got[0] && got[0].rawValue) { hand(got[0].rawValue); return; } } catch (e) {}
        setTimeout(tick, 180);
      };
      tick();
      return;
    }
  }
  try {
    scanStatus('scan.loadingReader');
    const ZX = await loadZxing();
    if (!S.el) return;
    const reader = new ZX.BrowserMultiFormatReader();
    const ctl = await reader.decodeFromVideoElement(v, (res) => { if (res && res.getText) hand(res.getText()); });
    S.stop = () => { try { ctl.stop(); } catch (e) {} };
    scanStatus('scan.aim');
  } catch (e) { scanStatus('scan.noReader'); }
}

function open(opts) {
  close();
  S.opts = opts || {}; S.done = false;
  const el = document.createElement('div');
  el.className = 'ra-scan' + (S.opts.drv ? ' drv' : ''); el.id = 'raScan'; el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true');
  el.innerHTML = `<div class="ra-scan-in">
    <div class="ra-scan-hd"><b>${esc(S.opts.title || t('scan.title'))}</b><button type="button" class="ra-scan-x" data-scan-close>${esc(t('scan.close'))}</button></div>
    <div class="ra-scan-cam"><video muted playsinline autoplay></video><div class="ra-scan-frame" aria-hidden="true"></div></div>
    <div class="ra-scan-status" data-scan-status role="status">${esc(t('scan.starting'))}</div>
    ${S.opts.hint ? `<div class="ra-scan-hint">${esc(S.opts.hint)}</div>` : ''}
    <form class="ra-scan-type" data-scan-form>
      <label for="raScanIn">${esc(t('scan.typeLabel'))}</label>
      <div class="ra-scan-row"><input id="raScanIn" autocomplete="off" autocapitalize="characters" spellcheck="false" maxlength="64" placeholder="${esc(t('scan.typePh'))}">
      <button type="submit" class="ra-scan-go">${esc(t('scan.lookUp'))}</button></div>
    </form></div>`;
  document.body.appendChild(el);
  S.el = el;
  el.querySelector('[data-scan-close]').addEventListener('click', close);
  el.querySelector('[data-scan-form]').addEventListener('submit', (e) => { e.preventDefault(); hand(el.querySelector('#raScanIn').value, 'typed'); });
  el.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  camera();
}

window.raScan = { open, close, loadZxing, formats: FORMATS.slice() };
